import { useState, useCallback, useEffect } from 'react';
import { toast } from '@/components/ui/use-toast';
import { SocialMediaPost } from '@/lib/api';
import { socialMediaApi } from '../api';

export type ComposerStep = 'compose' | 'adapt' | 'schedule' | 'publish';

/**
 * Composer state + actions, scoped to a Holder. All save/adapt/schedule/
 * publish calls thread the Holder through. When no Holder is selected
 * the callbacks short-circuit; the owning page renders the workspace
 * picker.
 */
export function usePostComposer(holderId: string | null, existingPost?: SocialMediaPost) {
  const [post, setPost] = useState<SocialMediaPost | null>(existingPost ?? null);
  const [content, setContent] = useState(existingPost?.originalContent ?? '');
  const [title, setTitle] = useState(existingPost?.title ?? '');
  const [notes, setNotes] = useState(existingPost?.notes ?? '');
  const [targetAccountIds, setTargetAccountIds] = useState<string[]>(
    existingPost?.targetAccountIds ?? [],
  );
  const [tags, setTags] = useState<string[]>(existingPost?.tags ?? []);

  // Sync state when existingPost loads asynchronously (e.g. after fetch in ComposerPage)
  useEffect(() => {
    if (!existingPost) return;
    setPost(existingPost);
    setContent(existingPost.originalContent);
    setTitle(existingPost.title ?? '');
    setNotes(existingPost.notes ?? '');
    setTargetAccountIds(existingPost.targetAccountIds);
    setTags(existingPost.tags ?? []);
  }, [existingPost]);

  const [step, setStep] = useState<ComposerStep>('compose');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveDraft = useCallback(
    async (overrideTargetAccountIds?: string[], mediaFileIds?: string[], link?: string | null) => {
      if (!holderId) return null;
      if (!content.trim()) return;
      setIsSaving(true);
      setError(null);
      const accountIds = overrideTargetAccountIds ?? targetAccountIds;
      try {
        if (post) {
          const updated = await socialMediaApi.updateSocialMediaPost(holderId, post.postId, {
            originalContent: content,
            targetAccountIds: accountIds,
            mediaFileIds,
            tags,
            title: title || null,
            notes: notes || null,
            link: link !== undefined ? link || null : undefined,
          });
          setPost(updated);
          toast({ description: 'Draft updated.' });
          return updated;
        } else {
          const created = await socialMediaApi.createSocialMediaPost(holderId, {
            originalContent: content,
            targetAccountIds: accountIds,
            mediaFileIds,
            tags,
            title: title || undefined,
            notes: notes || undefined,
            link: link || undefined,
          });
          setPost(created);
          toast({ description: 'Draft saved.' });
          return created;
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save draft';
        setError(message);
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [holderId, post, content, targetAccountIds, tags, title, notes],
  );

  const adaptContent = useCallback(
    async (
      platforms?: string[],
      context?: {
        tone?: string[];
        goal?: string[];
        audience?: string;
        link?: string;
        currentPlatformText?: Record<string, string>;
      },
      platformSettings?: Record<string, { format?: string }>,
      /** Explicit post ID — used when the post was just created in the same call chain
       *  so we don't need to wait for a React re-render to get post.postId. */
      overridePostId?: string,
    ) => {
      if (!holderId) return null;
      const postId = overridePostId ?? post?.postId;
      if (!postId) return null;
      setIsSaving(true);
      setError(null);
      try {
        const result = await socialMediaApi.adaptSocialMediaPost(
          holderId,
          postId,
          platforms,
          context,
          platformSettings,
        );
        setPost((prev) => (prev ? { ...prev, platformContent: result.platformContent } : prev));
        toast({
          title: 'Content adapted',
          description: 'AI-generated content is ready for review',
        });
        return result.platformContent;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to adapt content';
        setError(message);
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [holderId, post],
  );

  const schedulePost = useCallback(
    async (publishAt: string, overridePostId?: string) => {
      if (!holderId) return null;
      const postId = overridePostId ?? post?.postId;
      if (!postId) return null;
      setIsSaving(true);
      setError(null);
      try {
        const updated = await socialMediaApi.scheduleSocialMediaPost(holderId, postId, publishAt);
        setPost(updated);
        toast({
          title: 'Post scheduled',
          description: `Scheduled for ${new Date(publishAt).toLocaleString()}`,
        });
        return updated;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to schedule post';
        setError(message);
        toast({ title: 'Scheduling failed', description: message, variant: 'destructive' });
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [holderId, post],
  );

  const publishNow = useCallback(
    async (postId: string) => {
      if (!holderId) return null;
      setIsSaving(true);
      setError(null);
      try {
        const result = await socialMediaApi.publishSocialMediaPostNow(holderId, postId);
        const updated = await socialMediaApi.getSocialMediaPost(holderId, postId);
        setPost(updated);
        toast({
          title: 'Post published',
          description: 'Your post has been published successfully',
        });
        return result.results;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to publish post';
        setError(message);
        toast({ title: 'Publishing failed', description: message, variant: 'destructive' });
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [holderId],
  );

  const resetToDraft = useCallback(async () => {
    if (!holderId || !post) return null;
    setIsSaving(true);
    setError(null);
    try {
      const updated = await socialMediaApi.resetSocialMediaPostToDraft(holderId, post.postId);
      setPost(updated);
      toast({ title: 'Post reset to draft', description: 'You can now edit and republish' });
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset post to draft';
      setError(message);
      toast({ title: 'Error', description: message, variant: 'destructive' });
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [holderId, post]);

  return {
    post,
    content,
    setContent,
    title,
    setTitle,
    notes,
    setNotes,
    targetAccountIds,
    setTargetAccountIds,
    tags,
    setTags,
    step,
    setStep,
    isSaving,
    error,
    saveDraft,
    adaptContent,
    schedulePost,
    publishNow,
    resetToDraft,
  };
}
